import { BootstrapData } from '../../types'
import { BOOTSTRAP_DATA, PLATFORM_LOGGER, SERVICES_LOADER } from './moduleNames'
import { createServicesManager, createServicesMap } from '@wix/services-manager'
import { ServicesManager } from '@wix/services-manager/types'
import { serviceLoaders, serviceConfigs } from '../../services/serviceLoaders'
import { IPlatformLogger } from '@wix/thunderbolt-symbols'

export interface IServices {
	init: () => Promise<void>
	getService: ServicesManager['getService']
}

const ServicesLoader = (bootstrapData: BootstrapData, logger: IPlatformLogger): IServices => {
	const { serviceDefinitionToConfig } = bootstrapData
	let serviceManager: ServicesManager

	const serviceDefinitionToFactoryPromise = Promise.all(
		Object.keys(serviceDefinitionToConfig!).map((serviceDefinition) => {
			const factoryGetter = serviceLoaders[serviceDefinition]
			if (!factoryGetter) {
				logger.captureError(new Error(`Service ${serviceDefinition} is not supported`), {
					tags: {
						feature: 'platform',
					},
				})
				return Promise.resolve([serviceDefinition, () => {}])
			}
			return factoryGetter().then((implementation) => [serviceDefinition, implementation])
		})
	).then((servicesArray) => Object.fromEntries(servicesArray))

	return {
		init: async () => {
			const serviceDefinitionToFactory = await serviceDefinitionToFactoryPromise
			serviceManager = createServicesManager(
				createServicesMap(
					Object.entries(serviceDefinitionToConfig!).map(([definition, config]) => ({
						definition,
						config: serviceConfigs[definition] || config,
						impl: serviceDefinitionToFactory[definition].impl,
					}))
				)
			)
		},
		getService: (serviceDefinition) => {
			if (!serviceManager) {
				const error = new Error('Service manager is not initialized')
				logger.captureError(error, {
					tags: {
						feature: 'platform',
					},
				})
				throw error
			}
			return serviceManager.getService(serviceDefinition)
		},
	}
}

export default {
	factory: ServicesLoader,
	deps: [BOOTSTRAP_DATA, PLATFORM_LOGGER],
	name: SERVICES_LOADER,
}
